import { Shield, Star, ArrowRight, Users, TrendingUp, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import PublicShell from '@/components/layouts/PublicShell';

const AnlageberatungPage = () => {
  const strategieDepots = [
    {
      title: 'Nachhaltige Strategien',
      description: 'Fonds werden ausschließlich nach Nachhaltigkeitskriterien ausgewählt.',
      icon: Shield
    },
    {
      title: 'Konventionelle Strategien',
      description: 'Unterscheiden sich primär in der Höhe der Aktienquote.',
      icon: TrendingUp
    },
    {
      title: 'Renditeorientierte Strategie',
      description: 'Fokussiert auf maximale Rendite.',
      icon: Target
    },
    {
      title: 'Gemanagte ETF-Strategie',
      description: 'Strukturierte Investments über ETFs.',
      icon: Users
    }
  ];

  return (
    <PublicShell>
      {/* Hero Section - Exact Height */}
      <div className="relative" style={{height: '70vh'}}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{backgroundImage: 'url(/images/sv-skyline-day.jpg)'}}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-800/60 to-transparent"></div>

        <div className="relative z-10 h-full flex items-center px-16">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-8 leading-tight font-serif">
              Anlageberatung
            </h1>

            <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-10 max-w-3xl">
              Individuelle und zielgerichtete Beratung für Ihre Anlagestrategie
            </p>
          </div>
        </div>
      </div>

      {/* Green Stewardship Section */}
      <div className="bg-primary-800">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Left Side - Text (3 columns) */}
          <div className="lg:col-span-3 p-12 lg:p-20 flex items-center">
            <div className="max-w-2xl">
              <div className="w-16 h-16 mb-8">
                <Target className="w-16 h-16 text-white" />
              </div>

              <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight font-serif">
                Unsere <em className="italic">Anlagestrategien</em>
              </h2>

              <p className="text-xl text-primary-100 leading-relaxed">
                Wir bieten verschiedene Strategien, die auf Ihre individuellen Bedürfnisse und Risikotoleranz abgestimmt sind.
                Von nachhaltigen Investments bis hin zu renditeorientierten Ansätzen.
              </p>
            </div>
          </div>

          {/* Right Side - Image (2 columns) */}
          <div className="lg:col-span-2 relative min-h-96">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{backgroundImage: 'url(/images/sv-office-meeting.jpg)'}}
            ></div>
          </div>
        </div>
      </div>

      {/* Three White Cards Section - Overlapping */}
      <div className="relative -mt-16 z-20">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
          {/* Single Large White Box - Same Style as Statistics */}
          <div className="bg-white rounded-none shadow-[0_8px_40px_rgba(16,24,40,0.08)] w-full mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {/* Card 1 - Individuelle Beratung */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Individuelle Beratung</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Persönliche Beratung, die auf Ihre spezifischen Bedürfnisse und Ziele zugeschnitten ist.
                  Wir analysieren Ihre finanzielle Situation und entwickeln eine maßgeschneiderte Anlagestrategie.
                </p>
              </div>

              {/* Card 2 - Risikomanagement */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Risikomanagement</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Professionelles Risikomanagement zum Schutz Ihres Kapitals in volatilen Märkten.
                  Wir diversifizieren Ihr Portfolio intelligent und überwachen kontinuierlich die Risiken.
                </p>
              </div>

              {/* Card 3 - Langfristige Performance */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Langfristige Performance</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Fokus auf nachhaltige, langfristige Wertsteigerung Ihres Portfolios.
                  Unsere bewährten Strategien zielen auf stabile Erträge über verschiedene Marktzyklen ab.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Section - Strategy Details */}
      <div className="bg-primary-900 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Left Side - Text (3 columns) */}
          <div className="lg:col-span-3 p-12 lg:p-20 flex items-center">
            <div className="max-w-2xl">
              <div className="w-16 h-16 mb-8">
                <Star className="w-16 h-16 text-white" />
              </div>

              <h2 className="text-4xl md:text-5xl font-light text-gold-400 mb-8 leading-tight font-serif">
                Strategien für jeden <em className="italic">Anlegertyp</em>
              </h2>

              <p className="text-xl text-primary-100 leading-relaxed">
                Ob Sie auf Nachhaltigkeit setzen, maximale Rendite anstreben oder mit ETFs diversifizieren möchten -
                wir haben die passende Strategie für Ihre Ziele.
              </p>
            </div>
          </div>

          {/* Right Side - Image (2 columns) */}
          <div className="lg:col-span-2 relative min-h-96">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{backgroundImage: 'url(/images/sv-team-meeting.jpg)'}}
            ></div>
          </div>
        </div>
      </div>

      {/* Three White Cards Section - Strategy Types */}
      <div className="relative -mt-16 z-20">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
          {/* Single Large White Box */}
          <div className="bg-white rounded-none shadow-[0_8px_40px_rgba(16,24,40,0.08)] w-full mb-12">
            <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {strategieDepots.map((strategy, index) => {
                const IconComponent = strategy.icon;
                return (
                  <div key={index} className="px-16 py-12 text-left">
                    <div className="w-12 h-12 mb-6">
                      <IconComponent className="w-12 h-12 text-gold-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-4 leading-tight">{strategy.title}</h3>
                    <p className="text-[14px] text-slate-600 leading-relaxed">{strategy.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Green */}
      <div className="bg-primary-800 py-16">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit für Ihre persönliche Anlageberatung?
            </h2>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Vereinbaren Sie noch heute einen Termin mit unseren Experten und lassen Sie sich
              individuell beraten.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-primary-700 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-none shadow-lg border border-gray-200">
                <span className="text-primary-700 font-bold">Jetzt Beratungstermin vereinbaren</span>
                <ArrowRight className="ml-2 w-5 h-5 text-primary-700" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
};

export default AnlageberatungPage;
